/**
 * PostHog Analytics - centralized tracking for the entire app
 *
 * Usage:
 *   import { analytics } from '../utils/analytics';
 *   analytics.track('space_joined', { spaceId: '...', spaceName: '...' });
 *   analytics.identify(userId, { name: '...', email: '...' });
 */
import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

// 🔐 #10a (10-01): a seat secret never rides into PostHog. The room reads carried
// `?player_secret=` (and the LiveKit token `?playerSecret=`) in their URLs, and PostHog keeps
// URLs whole: every event's $current_url / $referrer, autocapture's link hrefs, a replay's
// network timing and console lines. These hooks rewrite only the VALUE
// (`player_secret=redacted`); the reads themselves move the secret to an `Authorization: Seat`
// header once the backend reads it (backend first). Pure, exported for the jest gate.
const SEAT_SECRET_HINT_RE = /player_?secret/i;
const SEAT_SECRET_PARAM_RE = /(^|[?&#;\s]|%26|%3F)(player_?secret)(=|%3D)[^&#\s"'<>]*/gi;

export function scrubSeatSecretText(text) {
  if (typeof text !== 'string' || !SEAT_SECRET_HINT_RE.test(text)) return text;
  return text.replace(SEAT_SECRET_PARAM_RE, '$1$2$3redacted');
}

function scrubSeatSecretDeep(value, depth = 0) {
  if (typeof value === 'string') return scrubSeatSecretText(value);
  if (!value || typeof value !== 'object' || depth > 8) return value;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) value[i] = scrubSeatSecretDeep(value[i], depth + 1);
  } else {
    for (const k of Object.keys(value)) value[k] = scrubSeatSecretDeep(value[k], depth + 1);
  }
  return value;
}

// before_send: every event's properties + person props. A replay batch ($snapshot) is DOM
// snapshots too big to walk; only its rrweb PLUGIN events (type 6: console lines, network
// timing) are scrubbed, and posthog never compresses those. If the check itself fails, the
// event is dropped rather than sent unchecked.
export function scrubSeatSecretEvent(event) {
  try {
    if (!event || typeof event !== 'object') return event;
    if (event.event === '$snapshot') {
      const batch = event.properties && event.properties.$snapshot_data;
      if (Array.isArray(batch)) {
        for (const e of batch) if (e && e.type === 6 && e.data) e.data = scrubSeatSecretDeep(e.data);
      }
      return event;
    }
    for (const k of ['properties', '$set', '$set_once']) if (event[k]) event[k] = scrubSeatSecretDeep(event[k]);
    return event;
  } catch (e) {
    return null;
  }
}

// session_recording.maskCapturedNetworkRequestFn: a network timing entry's `name` is its full
// URL, and posthog passes page URLs through here too (replay meta events), so never return
// null. Setting this replaces posthog's own body scrub: bodies stay off via recordBody: false.
export function maskSeatSecretRequest(request) {
  try {
    if (request && typeof request === 'object' && typeof request.name === 'string') request.name = scrubSeatSecretText(request.name);
  } catch (e) { /* a strange entry passes as is */ }
  return request;
}

/**
 * Initialize PostHog. Called once at app startup.
 */
export function initAnalytics() {
  if (initialized || !POSTHOG_KEY) {
    if (!POSTHOG_KEY) console.log('[Analytics] No PostHog key configured, skipping init');
    return;
  }

  // 5g.18 #1 — skip PostHog init in bot containers to prevent
  // posthog-recorder.js fetch failures from cascading into [[failed-to-fetch-cascade-masks-modal]]
  // skipReadyCheck path. Bot enters at /explore?bot=1 then navigates to /game-step URLs
  // without the flag — sessionStorage persists the detection across re-mounts.
  // Revert by removing this block if it doesn't help.
  if (typeof window !== 'undefined') {
    const urlHasBot = new URLSearchParams(window.location.search).get('bot') === '1';
    if (urlHasBot) {
      try { window.sessionStorage.setItem('__se_bot_mode', '1'); } catch (_) {}
    }
    const isBot = urlHasBot || (() => {
      try { return window.sessionStorage.getItem('__se_bot_mode') === '1'; } catch (_) { return false; }
    })();
    if (isBot) {
      console.log('[Analytics] bot mode detected, skipping PostHog init');
      return;
    }
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Automatic tracking
    autocapture: true,
    capture_pageview: false, // We handle this manually for SPA route changes
    capture_pageleave: true,
    // Session recording
    disable_session_recording: false,
    // 🔐 accounts (09-30 pre-ship review): never put request/response BODIES or headers
    // in a replay, whatever the PostHog project's remote network setting says. Its
    // built-in scrub misses the Google sign-in credential and the email in /api/auth
    // answers. Timing-only network capture (if the project has it on) is unchanged.
    session_recording: { recordBody: false, recordHeaders: false, maskCapturedNetworkRequestFn: maskSeatSecretRequest }, // 🔐 #10a
    before_send: scrubSeatSecretEvent, // 🔐 #10a: no seat secret in any event (above)
    // Performance
    loaded: (ph) => {
      // Disable in development to reduce noise (enable if you want dev tracking)
      if (process.env.NODE_ENV === 'development') {
        // Uncomment to disable in dev:
        // ph.opt_out_capturing();
      }
    },
    // Privacy
    respect_dnt: true,
    persistence: 'localStorage+cookie',
  });

  // 📊 Ledger 2.9: zero register() existed, so apex traffic was
  // indistinguishable from dev and no launch number was readable. Super
  // properties ride EVERY event from this device from now on.
  try {
    const host = window.location.hostname;
    posthog.register({
      deployment_host: host,
      apex: ['myspaice.ai', 'www.myspaice.ai', 'spaceexplorer.ai', 'www.spaceexplorer.ai'].includes(host),
    });
  } catch (_) { /* analytics is garnish, never breaks the app */ }

  initialized = true;
  console.log('[Analytics] PostHog initialized');
}

/**
 * Core analytics API - wraps PostHog with safety checks
 */
export const analytics = {
  /**
   * Track a custom event
   */
  track(event, properties = {}) {
    if (!initialized) return;
    posthog.capture(event, properties);
  },

  /**
   * Track a page view (call on route change)
   */
  pageView(path, properties = {}) {
    if (!initialized) return;
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path,
      ...properties,
    });
  },

  /**
   * Identify a user (call when user logs in or joins a space)
   */
  identify(userId, traits = {}) {
    if (!initialized) return;
    posthog.identify(userId, traits);
  },

  /**
   * Reset identity (call on logout)
   */
  reset() {
    if (!initialized) return;
    posthog.reset();
  },

  /**
   * Set properties on the current user without identifying
   */
  setPersonProperties(properties) {
    if (!initialized) return;
    posthog.people.set(properties);
  },

  /**
   * Associate the current user with a group (e.g., a space)
   */
  group(groupType, groupKey, groupProperties = {}) {
    if (!initialized) return;
    posthog.group(groupType, groupKey, groupProperties);
  },

  /**
   * Check if a feature flag is enabled
   */
  isFeatureEnabled(flagKey) {
    if (!initialized) return false;
    return posthog.isFeatureEnabled(flagKey);
  },

  /**
   * Get the PostHog instance for advanced usage
   */
  getInstance() {
    return initialized ? posthog : null;
  },
};

// ============================================================================
// Pre-defined event helpers for common actions
// ============================================================================

export const trackEvents = {
  // --- Space lifecycle ---
  spaceCreated: (props) => analytics.track('space_created', props),
  spaceJoined: (props) => analytics.track('space_joined', props),
  spaceLeft: (props) => analytics.track('space_left', props),
  spaceShared: (props) => analytics.track('space_shared', props),
  spaceCloned: (props) => analytics.track('space_cloned', props),
  spaceReset: (props) => analytics.track('space_reset', props),
  spaceRated: (props) => analytics.track('space_rated', props),

  // --- Game/round flow ---
  gameStarted: (props) => analytics.track('game_started', props),
  roundStarted: (props) => analytics.track('round_started', props),
  roundCompleted: (props) => analytics.track('round_completed', props),
  gameCompleted: (props) => analytics.track('game_completed', props),

  // --- Player actions ---
  messageSent: (props) => analytics.track('message_sent', props),
  interjectionSent: (props) => analytics.track('interjection_sent', props),
  spectatingMessageSent: (props) => analytics.track('spectating_message_sent', props),
  voteCast: (props) => analytics.track('vote_cast', props),
  reactionAdded: (props) => analytics.track('reaction_added', props),
  contentShared: (props) => analytics.track('content_shared', props),

  // --- Player join/identity ---
  playerJoined: (props) => analytics.track('player_joined', props),
  playerReadyUp: (props) => analytics.track('player_ready_up', props),
  playerSpectating: (props) => analytics.track('player_spectating', props),

  // --- Livestream ---
  livestreamJoined: (props) => analytics.track('livestream_joined', props),
  livestreamLeft: (props) => analytics.track('livestream_left', props),
  livestreamStarted: (props) => analytics.track('livestream_started', { ...props, type: props?.type || 'camera' }),
  livestreamStopped: (props) => analytics.track('livestream_stopped', props),

  // --- UI interactions ---
  tabChanged: (props) => analytics.track('tab_changed', props),
  themeApplied: (props) => analytics.track('theme_applied', props),
  musicToggled: (props) => analytics.track('music_toggled', props),

  // --- Explore/browse ---
  spaceViewed: (props) => analytics.track('space_viewed', props),
  spaceSearched: (props) => analytics.track('space_searched', props),
  categoryFiltered: (props) => analytics.track('category_filtered', props),

  // --- Errors ---
  errorOccurred: (props) => analytics.track('error_occurred', props),
};

export default analytics;
